import { ShoppingBag } from "lucide-react";
import type { Product, ProductVariant } from "@/types";
import { Badge, Button, Price, StarsRating } from "@/components/atoms";
import {
  VariantSelector,
  SubscribeToggle,
  QuantityStepper,
} from "@/components/molecules";
import {
  variantPrice,
  variantRegularPrice,
  subscribePrice,
  isOnSale,
} from "@/lib/product-helpers";

export interface BuyState {
  variant: ProductVariant;
  isSubscription: boolean;
  quantity: number;
  setVariant: (v: ProductVariant) => void;
  setIsSubscription: (s: boolean) => void;
  setQuantity: (q: number) => void;
  addToCart: () => void;
}

// Buy box: tag, title, rating, price, variant, subscribe toggle, qty, add-to-cart (frame 99:2).
export function ProductInfo({
  product,
  buy,
}: {
  product: Product;
  buy: BuyState;
}) {
  const unit = buy.isSubscription
    ? subscribePrice(product, buy.variant)
    : variantPrice(product, buy.variant);
  const regular = isOnSale(product)
    ? variantRegularPrice(product, buy.variant)
    : undefined;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-2">
        {product.condition && (
          <span className="font-condensed text-xs font-bold uppercase tracking-wider text-leaf-green-ink">
            {product.condition}
          </span>
        )}
        {product.badge && (
          <Badge kind={product.badge} accent={product.badgeAccent} />
        )}
      </div>

      <h1 className="font-condensed text-3xl font-bold leading-tight text-night-blue md:text-4xl">
        {product.name}
      </h1>

      <StarsRating
        value={product.ratingsAverage}
        count={product.ratingsCount}
        size={18}
      />

      <p className="text-night-blue/70">{product.longDescription}</p>

      <Price cents={unit} regularCents={regular} size="xl" />

      <VariantSelector
        product={product}
        selected={buy.variant}
        onSelect={buy.setVariant}
      />

      <SubscribeToggle
        isSubscription={buy.isSubscription}
        oneTimePrice={variantPrice(product, buy.variant)}
        subscribePrice={subscribePrice(product, buy.variant)}
        onChange={buy.setIsSubscription}
      />

      <div className="flex items-center gap-3">
        <QuantityStepper value={buy.quantity} onChange={buy.setQuantity} />
        <Button onClick={buy.addToCart} size="lg" fullWidth className="flex-1">
          <ShoppingBag size={18} /> Add to Cart
        </Button>
      </div>
    </div>
  );
}
